// Small presentational pieces used by the GameCanvas overlay layer.
import { useEffect, useState } from "react";
export function HudBlock({ label, value, subValue, color, testId }) {
  return (
    <div className="glass px-2 py-2 sm:px-4 sm:py-3 min-w-0" data-testid={testId}>
      <div className="font-mono-cosmic text-[8px] sm:text-[9px] tracking-[0.2em] sm:tracking-[0.3em] uppercase text-white/50 truncate">
        {label}
      </div>
      <div className="font-heading font-black text-sm sm:text-lg leading-none truncate" style={{ color }}>
        {value}
      </div>
      {subValue && (
        <div
          className="font-mono-cosmic text-[8px] sm:text-[10px] tracking-[0.1em] sm:tracking-[0.15em] uppercase text-white/70 mt-1 break-words leading-tight"
          title={subValue}
        >
          {subValue}
        </div>
      )}
    </div>
  );
}

export function FsHud({ label, value, subValue, color }) {
  return (
    <div className="pointer-events-auto glass px-3 py-1.5 min-w-[88px] max-w-[260px]">
      <div className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-white/50 leading-none">
        {label}
      </div>
      <div className="font-heading font-black text-sm leading-tight" style={{ color }}>
        {value}
      </div>
      {subValue && (
        <div
          className="font-mono-cosmic text-[9px] tracking-[0.15em] uppercase text-white/80 leading-tight mt-0.5 break-words whitespace-normal"
          title={subValue}
        >
          {subValue}
        </div>
      )}
    </div>
  );
}

/** Small chip rendered in the live HUD for an equipped artifact. */
export function ArtifactHudChip({ slot, art }) {
  if (!art) return null;
  // Power-specific status line.
  let status = `L${art.level}`;
  let accent = slot === "paddle" ? "#b026ff" : "#ffd700";
  switch (art.kind) {
    case "magnet":
      status = `L${art.level} · ${art.label}`;
      break;
    case "aegis":
      status = `L${art.level} · ${art.aegisCharges} save${art.aegisCharges === 1 ? "" : "s"} left`;
      accent = art.aegisCharges > 0 ? "#28e070" : "#666";
      break;
    case "laser":
      accent = "#7df9ff";
      if (typeof art.laserReadyIn === "number" && art.laserReadyIn > 0) {
        status = `L${art.level} · ${art.laserReadyIn.toFixed(1)}s to fire`;
      } else {
        status = `L${art.level} · READY · fires every ${art.label}`;
      }
      break;
    case "bomb":
      status = `L${art.level} · ${art.bombHits || 0}/${parseInt(String(art.label), 10) || 0} hits to boom`;
      accent = "#ff8c00";
      break;
    case "chain":
      status = `L${art.level} · ${art.label} chain`;
      accent = "#ff007f";
      break;
    case "phase":
      status = `L${art.level} · ${art.phaseCharges} pierce${art.phaseCharges === 1 ? "" : "s"} left`;
      accent = art.phaseCharges > 0 ? "#7df9ff" : "#666";
      break;
    default:
      break;
  }
  // Laser cooldown progress — a thin radial SVG ring around the icon.
  // 0 = ready (full ring), 1 = full cooldown (empty ring).
  const isLaser = art.kind === "laser";
  const cooldownPct = isLaser ? Math.min(1, Math.max(0, art.laserCooldownPct || 0)) : 0;
  const ringFill = 1 - cooldownPct;  // visible portion
  const circumference = 2 * Math.PI * 13;  // r=13
  return (
    <div
      className="glass px-3 py-1.5 flex items-center gap-2 flex-1 min-w-[160px]"
      data-testid={`artifact-hud-${slot}`}
      title={`${art.name} · ${status}`}
    >
      <div className="relative w-7 h-7 flex items-center justify-center shrink-0">
        {isLaser && (
          <svg
            className="absolute inset-0 -rotate-90"
            width="28"
            height="28"
            viewBox="0 0 28 28"
            aria-hidden
          >
            {/* Background track */}
            <circle
              cx="14"
              cy="14"
              r="13"
              fill="none"
              stroke="rgba(125, 249, 255, 0.18)"
              strokeWidth="2"
            />
            {/* Progress arc — fills as cooldown completes */}
            <circle
              cx="14"
              cy="14"
              r="13"
              fill="none"
              stroke={ringFill >= 1 ? "#7df9ff" : "#7df9ffcc"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - ringFill)}
              style={{
                transition: "stroke-dashoffset 220ms linear, stroke 220ms",
              }}
              data-testid={`artifact-hud-${slot}-laser-ring`}
            />
          </svg>
        )}
        <div className="text-base relative" aria-hidden>
          {art.icon}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase leading-none"
          style={{ color: accent + "cc" }}
        >
          {slot}
        </div>
        <div
          className="font-mono-cosmic text-[10px] tracking-[0.1em] uppercase truncate"
          style={{ color: accent }}
        >
          {art.name}
        </div>
        <div className="font-mono-cosmic text-[9px] text-white/55 truncate tabular-nums">
          {status}
        </div>
      </div>
    </div>
  );
}


/** Brief gold "AUTO-SAVED" pill that surfaces above the HUD for ~1.6s
 *  every time the Endless checkpoint POST resolves successfully.
 *  Gives the pilot a visible trust signal that their run progress
 *  is durable on the server — turns a silent resilience feature
 *  into a noticeable beat.
 *
 *  Lifecycle:
 *    - parent stamps `stampedAt = Date.now()` on each successful POST
 *    - this component schedules a fade-out via internal state at
 *      1100ms, fully unmounts at 1700ms (parent then re-renders the
 *      pill when a fresh stamp arrives)
 *
 *  Positioned absolutely so it doesn't disrupt the HUD 4-col grid.
 */
export function AutoSavedPill({ stampedAt }) {
  const [phase, setPhase] = useState("enter"); // enter → hold → exit
  useEffect(() => {
    setPhase("enter");
    const t1 = setTimeout(() => setPhase("hold"), 80);
    const t2 = setTimeout(() => setPhase("exit"), 1100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [stampedAt]);
  const opacity = phase === "exit" ? 0 : 1;
  const translate = phase === "enter" ? "translateY(4px)" : "translateY(0)";
  return (
    <div
      className="absolute -top-7 right-0 flex items-center gap-1.5 px-2.5 py-1 border border-[#ffd700]/55 bg-[#ffd700]/[0.10] pointer-events-none"
      style={{
        opacity,
        transform: translate,
        transition: "opacity 600ms ease-out, transform 200ms ease-out",
        boxShadow: "0 0 12px rgba(255,215,0,0.45)",
      }}
      data-testid="autosaved-pill"
    >
      <span
        className="w-1.5 h-1.5 bg-[#ffd700] rounded-full animate-pulse"
        aria-hidden
      />
      <span className="font-mono-cosmic text-[9px] tracking-[0.3em] text-[#ffd700] uppercase font-bold">
        // Auto-saved
      </span>
    </div>
  );
}


export function Overlay({ children, onBackdropClick, ...rest }) {
  const handleClick = onBackdropClick
    ? (e) => {
        // Only fire when the pilot clicks the dim backdrop itself, not
        // anything inside the glass card (skin row, shop, Resume btn).
        if (e.target === e.currentTarget) onBackdropClick(e);
      }
    : undefined;
  return (
    <div
      className="absolute inset-0 z-30 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center overflow-y-auto py-4"
      onClick={handleClick}
      {...rest}
    >
      <div
        className="glass p-6 sm:p-10 flex flex-col items-center max-h-full overflow-y-auto w-[min(92%,32rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
